/**
 * Brand palette presets derived from the official brand kit
 * (public/images/brand/README.md, "Approved Color Combinations").
 *
 * Each preset maps one approved combination onto the MUI palette:
 * - `light.primaryMain` is a contrast-safe variant of the combo accent (WCAG AA >= 4.5:1
 *   with white text), following the existing theme convention where `.light` carries the
 *   true decorative brand hex.
 * - `dark.primaryMain` is a light-enough variant to carry dark text on dark surfaces.
 *
 * Currently applied only to the Super Admin portal (see SuperAdminThemeScope); designed
 * so other role views (e.g. Account Manager) can opt in later by wrapping their route
 * tree in the same scope.
 */

export const BRAND_COLORS = {
  periwinkle: '#acb3d8',
  chartreuse: '#969646',
  postIt: '#f9e375',
  lavender: '#e1cee2',
  wisteria: '#8e6ba5',
  chalkboard: '#4c4d4d',
}

export const DEFAULT_PALETTE_ID = 'default'

/** Default (non-preset) theme swatch: AA-safe wisteria primary + lavender-tint background. */
export const DEFAULT_PALETTE_SWATCH = ['#6B5A9E', '#F5F3F7']

const DARK_CONTRAST_TEXT = '#1A202C'

export const PALETTE_PRESETS = [
  {
    id: 'periwinkle',
    label: 'Periwinkle',
    comboNote: 'Periwinkle with white — primary brand combination',
    swatch: [BRAND_COLORS.periwinkle, '#ffffff'],
    light: {
      primaryMain: '#545c8f',
      primaryLight: BRAND_COLORS.periwinkle,
      primaryDark: '#474e7a',
      primaryContrastText: '#FFFFFF',
      backgroundDefault: '#eef0f8',
    },
    dark: {
      primaryMain: BRAND_COLORS.periwinkle,
      primaryLight: '#c5cae6',
      primaryDark: '#8f97c4',
      primaryContrastText: DARK_CONTRAST_TEXT,
    },
  },
  {
    id: 'chartreuse',
    label: 'Chartreuse',
    comboNote: 'Chartreuse with white — text-safe background',
    swatch: [BRAND_COLORS.chartreuse, '#ffffff'],
    light: {
      primaryMain: '#6f6f34',
      primaryLight: BRAND_COLORS.chartreuse,
      primaryDark: '#5d5d2c',
      primaryContrastText: '#FFFFFF',
      backgroundDefault: '#f4f4e6',
    },
    dark: {
      primaryMain: BRAND_COLORS.chartreuse,
      primaryLight: '#c4c489',
      primaryDark: '#7d7d3b',
      primaryContrastText: DARK_CONTRAST_TEXT,
    },
  },
  {
    id: 'post-it',
    label: 'Post-it Note',
    comboNote: 'Post-it with chalkboard accents',
    swatch: [BRAND_COLORS.postIt, BRAND_COLORS.chalkboard],
    light: {
      primaryMain: BRAND_COLORS.chalkboard,
      primaryLight: BRAND_COLORS.postIt,
      primaryDark: '#3a3b3b',
      primaryContrastText: '#FFFFFF',
      backgroundDefault: '#fdf8e0',
    },
    dark: {
      primaryMain: BRAND_COLORS.postIt,
      primaryLight: '#fbeda0',
      primaryDark: '#e5c94d',
      primaryContrastText: DARK_CONTRAST_TEXT,
    },
  },
  {
    id: 'lavender',
    label: 'Lavender',
    comboNote: 'Lavender with chalkboard accents',
    swatch: [BRAND_COLORS.lavender, BRAND_COLORS.chalkboard],
    light: {
      primaryMain: BRAND_COLORS.chalkboard,
      primaryLight: BRAND_COLORS.lavender,
      primaryDark: '#3a3b3b',
      primaryContrastText: '#FFFFFF',
      backgroundDefault: '#f8f2f8',
    },
    dark: {
      primaryMain: BRAND_COLORS.lavender,
      primaryLight: '#eedfee',
      primaryDark: '#c9aecb',
      primaryContrastText: DARK_CONTRAST_TEXT,
    },
  },
  {
    id: 'wisteria',
    label: 'Wisteria',
    comboNote: 'Wisteria with white — text-safe background',
    swatch: [BRAND_COLORS.wisteria, '#ffffff'],
    light: {
      primaryMain: '#7e5d94',
      primaryLight: BRAND_COLORS.wisteria,
      primaryDark: '#6d4f82',
      primaryContrastText: '#FFFFFF',
      backgroundDefault: '#f4eff7',
    },
    dark: {
      primaryMain: '#b79ec9',
      primaryLight: '#d0bfdc',
      primaryDark: BRAND_COLORS.wisteria,
      primaryContrastText: DARK_CONTRAST_TEXT,
    },
  },
  {
    id: 'chalkboard',
    label: 'Chalkboard',
    comboNote: 'Chalkboard with lavender — text-safe background',
    swatch: [BRAND_COLORS.chalkboard, BRAND_COLORS.lavender],
    light: {
      primaryMain: BRAND_COLORS.chalkboard,
      primaryLight: BRAND_COLORS.lavender,
      primaryDark: '#3a3b3b',
      primaryContrastText: '#FFFFFF',
      backgroundDefault: '#f0f0f0',
    },
    dark: {
      primaryMain: '#b0b0b0',
      primaryLight: '#cfcfcf',
      primaryDark: '#8f9090',
      primaryContrastText: DARK_CONTRAST_TEXT,
    },
  },
]

/** @returns the preset for `id`, or null for the default/unknown palette. */
export function getPalettePreset(id) {
  if (!id || id === DEFAULT_PALETTE_ID) return null
  return PALETTE_PRESETS.find((preset) => preset.id === id) || null
}

/** Normalize a persisted palette id; unknown values fall back to default. */
export function normalizePaletteId(id) {
  return getPalettePreset(id) ? id : DEFAULT_PALETTE_ID
}

/**
 * WCAG 2.x relative luminance + contrast ratio. Used by tests to enforce AA on
 * every preset; exported so future presets stay honest.
 */
export function contrastRatio(hexA, hexB) {
  const lumA = relativeLuminance(hexA)
  const lumB = relativeLuminance(hexB)
  const [hi, lo] = lumA >= lumB ? [lumA, lumB] : [lumB, lumA]
  return (hi + 0.05) / (lo + 0.05)
}

function relativeLuminance(hex) {
  const value = String(hex).replace('#', '')
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value
  const [r, g, b] = [0, 2, 4].map((i) => {
    const channel = parseInt(full.slice(i, i + 2), 16) / 255
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
